import React, { RefObject, useCallback } from 'react';

import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';

import { useLoginNudge } from './use-login-nudge';
import {
  TextContainer,
  LoginLink,
  SocialLoginContainer,
  SocialButtonContainer,
  StyledTooltip,
} from './login-nudge.styles';
import { AppleIcon, GoogleIcon } from './icons';
import { useLoginNudgePosition } from './use-login-nudge-position';

type LoginNudgeProps = {
  anchorEl: RefObject<HTMLElement> | null;
  containerRef: RefObject<HTMLElement> | null;
};

export const LoginNudge = ({ anchorEl, containerRef }: LoginNudgeProps): JSX.Element | null => {
  const { dismissNudge, handleNudgeClick, shouldShowNudge } = useLoginNudge();
  const { position } = useLoginNudgePosition({ anchorEl, containerRef });
  // Non-Pay menu-header surface: stamp an existing session token but never mint one just to emit the nudge event.
  const { trackLoginNudge } = useDutchiePayAnalytics({ mintSessionToken: false });
  const onImpression = useCallback(
    () => trackLoginNudge({ action: 'viewed', variant: 'header_social_login' }),
    [trackLoginNudge]
  );
  const onClose = useCallback(() => {
    dismissNudge();
    trackLoginNudge({ action: 'dismissed', variant: 'header_social_login' });
  }, [dismissNudge, trackLoginNudge]);
  if (!shouldShowNudge || !position || !anchorEl?.current || !containerRef?.current) {
    return null;
  }

  return (
    <StyledTooltip
      dataTestId='login-nudge'
      ariaLabel='Login Nudge'
      position={position}
      onClose={onClose}
      onContentClick={handleNudgeClick}
      onImpression={onImpression}
    >
      <TextContainer>
        <LoginLink>Login</LoginLink> for recommendations & re‑ordering of your favorites
      </TextContainer>
      <SocialLoginContainer>
        <SocialButtonContainer aria-label='Login with Google'>
          <GoogleIcon />
        </SocialButtonContainer>
        <SocialButtonContainer aria-label='Login with Apple'>
          <AppleIcon />
        </SocialButtonContainer>
      </SocialLoginContainer>
    </StyledTooltip>
  );
};
